/**
 * /auth/oauth/callback — Página de aterrizaje del OAuth flow.
 *
 * tax-account puede redirigir aquí en TRES shapes distintos del fragment:
 *
 * 1. Login OK:
 *    /auth/oauth/callback?intent=login#access_token=…&refresh_token=…&…
 *
 * 2. MFA requerido (user con TOTP enrolled): el OAuth solo verifica el
 *    primer factor. tax-account envía un challenge token; el SPA debe
 *    pasar al step 2 (TOTP form). Re-usamos el flow del LoginPage que ya
 *    soporta `location.state.mfaChallenge` (sprint I.3).
 *    /auth/oauth/callback?intent=login#mfa_required=1&mfa_challenge_token=…&email=…
 *
 * 3. Error:
 *    /auth/oauth/callback?intent=login#error=oauth_error
 *
 * Para los casos 1 y 3, comportamiento simple. Para 2, redirige a
 * /auth/login pasando el challenge en navigate state.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { parseOAuthCallbackHash, setAccessToken } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { AUTH_ME_QUERY_KEY } from '@/hooks/use-auth-context'

const ERROR_MESSAGES: Record<string, string> = {
  oauth_error: 'OAuth falló — proveedor no respondió correctamente',
  oauth_state_mismatch: 'Sesión OAuth inválida (CSRF check). Reintenta.',
  oauth_provider_error: 'El proveedor (Google/Microsoft) rechazó la autorización',
  oauth_email_unverified: 'Tu email en el proveedor no está verificado',
  user_not_registered: 'No existe cuenta con ese email. Contáctanos para alta.',
}

export function OAuthCallbackPage() {
  const nav = useNavigate()
  const qc = useQueryClient()

  useEffect(() => {
    const parsed = parseOAuthCallbackHash(window.location.hash)
    // Limpiar hash de la URL (no queremos token ni challenge en history).
    window.history.replaceState(null, '', window.location.pathname + window.location.search)

    if (!parsed) {
      toast.error('Callback OAuth sin datos')
      nav(ROUTES.LOGIN, { replace: true })
      return
    }

    if (parsed.error) {
      toast.error(ERROR_MESSAGES[parsed.error] ?? `OAuth: ${parsed.error}`)
      nav(ROUTES.LOGIN, { replace: true })
      return
    }

    // MFA requerido — handoff al LoginPage que ya tiene el form TOTP.
    // Detectar por mfa_challenge_token (no por mfa_required, que es string
    // "1" pero podría faltar si el backend cambia el shape).
    if (parsed.mfa_challenge_token) {
      nav(ROUTES.LOGIN, {
        replace: true,
        state: {
          mfaChallenge: {
            token: parsed.mfa_challenge_token,
            expiresIn: parsed.mfa_challenge_expires_in ?? 300,
            email: parsed.email ?? '',
          },
        },
      })
      return
    }

    if (!parsed.access_token) {
      toast.error('Callback OAuth sin access_token')
      nav(ROUTES.LOGIN, { replace: true })
      return
    }

    setAccessToken(parsed.access_token)
    // Invalidar /me cache: el token es nuevo, hidrata desde cero.
    qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    toast.success('Sesión iniciada')
    // Facturador standalone: directo al dashboard (sin RoleRedirect).
    nav(ROUTES.DASHBOARD, { replace: true })
  }, [nav, qc])

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app">
      <div className="text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-600" />
        <p className="mt-3 text-sm text-fg-secondary">Procesando autenticación...</p>
      </div>
    </div>
  )
}
